import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

export interface GlTransactionDetailData {
  type: string;
  typeid?: number | string | null;
  number: string;
  trandate: string;
  debit: number;
  credit: number;
  chequeno: string;
  narrative: string;
  balance: number;
  tag: string;
  periodno?: number | string;
  periodname?: string;
  counterindex?: number;
  tagref?: number | null;
  debtorno?: string | null;
  debtorname?: string | null;
}

interface GlJournalLine {
  accountcode: string;
  accountname: string;
  narrative: string;
  debit: number | string;
  credit: number | string;
}

@Component({
	selector: 'app-gl-transaction-detail-modal',
	templateUrl: './transaction-detail-modal.component.html',
	styleUrls: ['./transaction-detail-modal.component.scss']
})
export class TransactionDetailModalComponent implements OnInit {
	@ViewChild('tagRoot') tagRoot?: ElementRef<HTMLElement>;
	@ViewChild('tagTriggerBtn') tagTriggerBtn?: ElementRef<HTMLElement>;

	canEdit = false;
	editMode = false;
	tags: { tagId: number; description: string }[] = [];
	editForm: FormGroup;
	saving = false;
	tagDropdownOpen = false;
	tagPanelStyle: Record<string, string> = {};
	journalLines: GlJournalLine[] = [];
	journalLoading = false;
	journalError = '';

	constructor(
		public dialogRef: MatDialogRef<TransactionDetailModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: GlTransactionDetailData,
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef
	) {
		const tr = this.data.tagref;
		const tn = tr != null ? Number(tr) : null;
		const initialTag = tn != null && !Number.isNaN(tn) && tn !== 0 ? tn : null;
		this.editForm = this.fb.group({
			narrative: [this.data.narrative ?? ''],
			tagref: [initialTag]
		});
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(ev: MouseEvent): void {
		if (!this.tagDropdownOpen) return;
		const root = this.tagRoot?.nativeElement;
		if (root && !root.contains(ev.target as Node)) {
			this.tagDropdownOpen = false;
		}
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.tagDropdownOpen) {
			this.tagDropdownOpen = false;
		}
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (this.tagDropdownOpen) {
			this.tagDropdownOpen = false;
		}
	}

	get tagSelectedLabel(): string {
		const raw = this.editForm.get('tagref')?.value;
		if (raw === null || raw === undefined || raw === '' || Number(raw) === 0) {
			return '— None —';
		}
		const id = Number(raw);
		const t = this.tags.find((x) => x.tagId === id);
		return t?.description ?? `Tag #${id}`;
	}

	toggleTagDropdown(ev: MouseEvent): void {
		ev.stopPropagation();
		if (this.tagDropdownOpen) {
			this.tagDropdownOpen = false;
			return;
		}
		const el = this.tagTriggerBtn?.nativeElement;
		if (!el) {
			this.tagDropdownOpen = true;
			return;
		}
		const rect = el.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom - 8;
		const openUp = spaceBelow < 100 && rect.top > 120;
		const maxH = 260;
		if (openUp) {
			const h = Math.min(maxH, rect.top - 12);
			this.tagPanelStyle = {
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				bottom: `${window.innerHeight - rect.top + 4}px`,
				maxHeight: `${h}px`
			};
		} else {
			const h = Math.min(maxH, spaceBelow);
			this.tagPanelStyle = {
				top: `${rect.bottom + 4}px`,
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				maxHeight: `${h}px`
			};
		}
		this.tagDropdownOpen = true;
	}

	isTagOptionActive(tagId: number | null): boolean {
		const raw = this.editForm.get('tagref')?.value;
		if (tagId === null) {
			return raw === null || raw === undefined || raw === '' || Number(raw) === 0;
		}
		return Number(raw) === tagId;
	}

	selectTagRef(tagId: number | null): void {
		this.editForm.patchValue({ tagref: tagId });
		this.tagDropdownOpen = false;
	}

	ngOnInit(): void {
		this.canEdit = this.data.counterindex != null;
		this.loadTags();
		this.loadJournalLines();
	}

	asNumber(value: number | string): number {
		return typeof value === 'number' ? value : Number(value || 0);
	}

	get journalTotalDebit(): number {
		return this.journalLines.reduce((sum, line) => sum + this.asNumber(line.debit), 0);
	}

	get journalTotalCredit(): number {
		return this.journalLines.reduce((sum, line) => sum + this.asNumber(line.credit), 0);
	}

	private loadJournalLines(): void {
		const typeid = this.data.typeid;
		const typeno = this.data.number;
		const typeNum = typeid != null && typeid !== '' ? Number(typeid) : NaN;
		const transNum = typeno != null && typeno !== '' ? Number(typeno) : NaN;
		if (Number.isNaN(typeNum) || Number.isNaN(transNum)) {
			return;
		}
		this.journalLoading = true;
		this.generalLedgerService.getGlTransDetails({ type: typeNum, typeno: transNum }).subscribe({
			next: (res: any) => {
				this.journalLoading = false;
				this.journalLines = Array.isArray(res?.lines) ? res.lines : [];
				this.journalError = this.journalLines.length ? '' : (res?.message || 'No GL lines found for this journal.');
				this.cdr.markForCheck();
			},
			error: () => {
				this.journalLoading = false;
				this.journalError = 'Unable to load full GL journal.';
				this.cdr.markForCheck();
			}
		});
	}

	private loadTags(): void {
		this.generalLedgerService.getTags().subscribe({
			next: (r: any) => {
				this.tags = (r?.tags || [])
					.map((t: any) => ({
						tagId: Number(t.tagId),
						description: String(t.description ?? '')
					}))
					.filter((t: { tagId: number; description: string }) => !Number.isNaN(t.tagId) && t.tagId !== 0);
				this.cdr.markForCheck();
				this.cdr.detectChanges();
			},
			error: () => {
				this.tags = [];
				this.cdr.markForCheck();
			}
		});
	}

	close(): void {
		this.dialogRef.close();
	}

	startEdit(): void {
		const tr = this.data.tagref;
		const tagNum = tr != null ? Number(tr) : null;
		const effectiveTag =
			tagNum != null && !Number.isNaN(tagNum) && tagNum !== 0 ? tagNum : null;
		this.tagDropdownOpen = false;
		this.editForm.patchValue({
			narrative: this.data.narrative ?? '',
			tagref: effectiveTag
		});
		this.editMode = true;
		this.cdr.detectChanges();
	}

	cancelEdit(): void {
		this.tagDropdownOpen = false;
		this.editMode = false;
	}

	save(): void {
		const counterindex = this.data.counterindex;
		if (counterindex == null) return;
		const narrative = this.editForm.get('narrative')?.value ?? '';
		const tagref = this.editForm.get('tagref')?.value;
		const tagToSave =
			tagref !== undefined && tagref !== '' && tagref != null && Number(tagref) !== 0
				? tagref
				: null;
		this.saving = true;
		this.generalLedgerService.updateGlTransLine({
			counterindex,
			narrative: narrative.trim(),
			tag: tagToSave
		}).subscribe({
			next: () => {
				this.saving = false;
				this.globalSearchService.showNotification('Transaction line updated.', 'success', 'bottom', 'right');
				this.dialogRef.close({ saved: true });
			},
			error: () => {
				this.saving = false;
				this.globalSearchService.showNotification('Error updating transaction line.', 'danger', 'bottom', 'right');
			}
		});
	}
}

